/**
 * Argon2id KEK derivation.
 *
 * Runs entirely in the client (browser, never on Cloudflare Worker). hash-wasm
 * ships a ~50 KB WASM blob that we self-host under /argon2.wasm; the client
 * fetches it on first login and caches via service worker / standard browser
 * cache.
 *
 * Parameters (OWASP 2024 baseline):
 *   memory     = 64 MiB (m = 65536 KiB)
 *   iterations = 3
 *   parallelism = 1
 *   hashLength = 32 (AES-256 key material)
 *
 * Salt: 16 bytes, stored plaintext on users.kdf_salt. Rotated on every
 * master-password change so the wrapped_ask cannot be replayed under the
 * old salt.
 *
 * Output: branded RawSymmetricKey (raw 32 bytes). NEVER persisted at rest.
 * Caller imports it into SubtleCrypto via importKey if it needs an AES key
 * handle.
 */

import { argon2id as wasmArgon2id, type IDataType } from "hash-wasm";

import { randomBytes, utf8Encode } from "./bytes.js";
import { asBrand, DEFAULT_ARGON2ID_PARAMS } from "../types/index.js";
import type {
  Argon2idParams,
  KdfSaltBytes,
  RawSymmetricKey,
} from "../types/index.js";

/** Generate a fresh KDF salt (16 random bytes). */
export function generateKdfSalt(): KdfSaltBytes {
  return asBrand<KdfSaltBytes>(randomBytes(16));
}

/**
 * NFC-normalize a password string before any KDF.
 *
 * Without this, "niccolò" (NFC) and "niccòlo" (NFD) derive different
 * KEKs even though they look identical. Mandatory per the workflow review
 * (attack A6.1).
 */
export function normalizePassword(password: string): string {
  return password.normalize("NFC");
}

export interface DeriveKekInput {
  readonly password: string;
  readonly salt: KdfSaltBytes;
  readonly params?: Argon2idParams;
}

/**
 * Derive a KEK from master password + salt using Argon2id.
 *
 * Implementation note: hash-wasm returns the raw hash bytes when called with
 * `outputType: "binary"`. We pass the password as UTF-8 bytes (after NFC
 * normalization) and let hash-wasm convert internally. Both Node 20+ and
 * browsers run the WASM in ~400 ms with the default params on commodity
 * hardware.
 */
export async function deriveKek(
  input: DeriveKekInput,
): Promise<RawSymmetricKey> {
  const params = input.params ?? DEFAULT_ARGON2ID_PARAMS;
  const passwordBytes = utf8Encode(normalizePassword(input.password));

  const hash = await wasmArgon2id({
    password: passwordBytes as unknown as IDataType,
    salt: input.salt as unknown as IDataType,
    parallelism: params.p,
    iterations: params.t,
    memorySize: params.m,
    hashLength: params.hashLength,
    outputType: "binary",
  });

  if (!(hash instanceof Uint8Array)) {
    throw new TypeError("argon2id: expected Uint8Array output");
  }
  if (hash.length !== params.hashLength) {
    throw new RangeError(
      `argon2id: expected ${params.hashLength} bytes, got ${hash.length}`,
    );
  }
  return asBrand<RawSymmetricKey>(hash);
}
